import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Handshake, Plus, Mail, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { DocScanButton } from "@/components/DocScanButton";

const COLUMNS = [
  { key: "potenziell", label: "Potenziell", className: "bg-neutral-50 border-neutral-200" },
  { key: "in_kontakt", label: "In Kontakt", className: "bg-amber-50/50 border-amber-200" },
  { key: "partner", label: "Aktive Partner", className: "bg-green-50/50 border-green-200" },
];

const REACH = {
  high: { label: "Hohe Reichweite", className: "bg-blue-100 text-blue-700 border-blue-200" },
  medium: { label: "Mittel", className: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  low: { label: "Niedrig", className: "bg-neutral-100 text-neutral-500 border-neutral-200" },
};

export default function PartnerRadar() {
  const { accountKey, search } = useOutletContext();
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/partners", { params: { account: accountKey } }).then(({ data }) => setPartners(data));
  useEffect(() => { if (accountKey) load(); }, [accountKey]);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    return q ? partners.filter((p) => p.name.toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q)) : partners;
  }, [partners, search]);

  const move = async (p, status) => {
    setPartners((prev) => prev.map((x) => (x.id === p.id ? { ...x, status } : x)));
    try {
      await api.patch(`/partners/${p.id}`, { status });
      toast.success(`${p.name} → ${COLUMNS.find((c) => c.key === status).label}`);
    } catch {
      toast.error("Aktualisierung fehlgeschlagen");
      load();
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1300px] mx-auto animate-rise">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400 flex items-center gap-1.5">
            <Handshake className="h-3.5 w-3.5" /> Business-/Partner-Radar
          </p>
          <h1 className="font-head text-3xl font-bold text-neutral-900 tracking-tight mt-1">Partner & Kooperationen</h1>
        </div>
        <CreatePartnerDialog open={open} setOpen={setOpen} accountKey={accountKey} onCreated={load} />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        {COLUMNS.map((col) => {
          const items = filtered.filter((p) => p.status === col.key);
          return (
            <div key={col.key} data-testid={`partner-col-${col.key}`} className={`rounded-xl border ${col.className} p-3`}>
              <div className="flex items-center justify-between px-1.5 py-1">
                <span className="font-head font-semibold text-sm text-neutral-800">{col.label}</span>
                <span className="text-xs font-semibold text-neutral-400">{items.length}</span>
              </div>
              <div className="mt-2 space-y-2.5">
                {items.map((p) => {
                  const reach = REACH[p.reach_potential] || REACH.medium;
                  return (
                    <div key={p.id} data-testid={`partner-card-${p.id}`} className="bg-white border border-neutral-200 rounded-lg p-3.5 hover:shadow-md transition">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-neutral-900 text-[15px]">{p.name}</h4>
                        {p.reach_potential === "high" && <Zap className="h-4 w-4 text-blue-500 shrink-0" />}
                      </div>
                      {p.category && <div className="text-xs text-neutral-400 mt-0.5">{p.category}</div>}
                      {p.notes && <p className="text-[13px] text-neutral-500 mt-2 leading-snug">{p.notes}</p>}
                      <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${reach.className}`}>{reach.label}</span>
                        {p.contact && (
                          <span className="text-[11px] text-neutral-400 flex items-center gap-1"><Mail className="h-3 w-3" /> {p.contact}</span>
                        )}
                      </div>
                      <div className="mt-3 pt-2.5 border-t border-neutral-100 flex gap-1.5">
                        {COLUMNS.filter((c) => c.key !== p.status).map((c) => (
                          <button
                            key={c.key}
                            data-testid={`partner-move-${p.id}-${c.key}`}
                            onClick={() => move(p, c.key)}
                            className="text-[11px] font-medium px-2 py-1 rounded-md bg-neutral-100 text-neutral-600 hover:bg-neutral-200 transition"
                          >
                            → {c.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {items.length === 0 && <p className="text-xs text-neutral-400 px-1.5 py-3">Keine Einträge.</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CreatePartnerDialog({ open, setOpen, accountKey, onCreated }) {
  const [form, setForm] = useState({ name: "", category: "", status: "potenziell", reach_potential: "medium", contact: "", notes: "" });
  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const cls = "w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400";
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name erforderlich");
    await api.post("/partners", { account_key: accountKey, ...form });
    toast.success("Partner angelegt");
    setOpen(false);
    setForm({ name: "", category: "", status: "potenziell", reach_potential: "medium", contact: "", notes: "" });
    onCreated();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-partner-btn" className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800">
          <Plus className="h-4 w-4" /> Neuer Partner
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-head">Neuer Partner</DialogTitle>
          <DialogDescription>Erfasse einen Partner oder eine potenzielle Kooperation.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <DocScanButton kind="partner" onExtracted={(d) => setForm((s) => ({ ...s, ...d }))} />
          <input data-testid="partner-name-input" placeholder="Name" value={form.name} onChange={(e) => f("name", e.target.value)} className={cls} />
          <input placeholder="Kategorie (z. B. Handel)" value={form.category} onChange={(e) => f("category", e.target.value)} className={cls} />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.status} onChange={(e) => f("status", e.target.value)} className={`${cls} bg-white`}>
              <option value="potenziell">Potenziell</option>
              <option value="in_kontakt">In Kontakt</option>
              <option value="partner">Aktiver Partner</option>
            </select>
            <select value={form.reach_potential} onChange={(e) => f("reach_potential", e.target.value)} className={`${cls} bg-white`}>
              <option value="high">Hohe Reichweite</option>
              <option value="medium">Mittel</option>
              <option value="low">Niedrig</option>
            </select>
          </div>
          <input placeholder="Kontakt (E-Mail)" value={form.contact} onChange={(e) => f("contact", e.target.value)} className={cls} />
          <textarea rows={3} placeholder="Notizen" value={form.notes} onChange={(e) => f("notes", e.target.value)} className={`${cls} h-auto py-2 resize-none`} />
        </div>
        <DialogFooter>
          <button data-testid="partner-save-btn" onClick={submit} className="bg-neutral-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-neutral-800">Partner speichern</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
